import React, { useEffect, useMemo, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography
} from "@mui/material";
import api from "../api";
import PageHeader from "./PageHeader";

const levelColors = ["default", "warning", "warning", "warning", "warning", "warning"];
const buildSummaryFromCompetences = (competences = []) => {
  const competencesTotal = competences.length;
  const competencesEvaluees = competences.filter((competence) => competence.isEvaluated).length;
  const totalValeur = competences.reduce((sum, competence) => sum + Number(competence.valeur || 0), 0);
  const totalPoints = competences.reduce((sum, competence) => sum + Number(competence.pointsObtenus || 0), 0);
  const isComplete = competencesTotal > 0 && competencesEvaluees === competencesTotal;
  const computedNoteSur20 = totalValeur > 0 ? Math.round(((totalPoints / totalValeur) * 20) * 100) / 100 : null;

  return {
    competencesTotal,
    competencesEvaluees,
    totalValeur,
    totalPoints,
    isComplete,
    computedNoteSur20,
  };
};

export default function EleveSuiviDashboard() {
  const { id } = useParams();
  const location = useLocation();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const activityQuery = query.get("activity");

  const [eleve, setEleve] = useState(null);
  const [activites, setActivites] = useState([]);
  const [currentActivite, setCurrentActivite] = useState(null);
  const [competences, setCompetences] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    setError("");

    async function fetchAll() {
      try {
        const [resEleve, resActivites] = await Promise.all([
          api.get(`/eleves/${id}`),
          api.get(`/activites`)
        ]);

        setEleve(resEleve.data);
        const acts = Array.isArray(resActivites.data) ? resActivites.data : [];
        setActivites(acts);

        let selected = null;
        if (activityQuery) {
          const decodedActivityQuery = decodeURIComponent(String(activityQuery || ""));
          const qLower = String(activityQuery || "").toLowerCase();
          const qDecodedLower = decodedActivityQuery.toLowerCase();
          selected = acts.find((a) =>
            String(a._id) === activityQuery ||
            String(a._id) === decodedActivityQuery ||
            String(a.nom || "").toLowerCase() === qLower ||
            String(a.nom || "").toLowerCase() === qDecodedLower
          );
        }

        if (!selected && acts.length > 0) selected = acts[0];
        setCurrentActivite(selected || null);
      } catch (err) {
        setError(err.response?.data?.error || "Élève introuvable.");
      } finally {
        setLoading(false);
      }
    }

    if (id) fetchAll();
  }, [id, activityQuery]);

  useEffect(() => {
    async function fetchSuivi() {
      if (!currentActivite || !eleve) return;
      setLoading(true);
      setError("");
      try {
        const suiviRes = await api.get(`/elevesuivi/${id}/${currentActivite._id}`);
        const loadedCompetences = Array.isArray(suiviRes.data) ? suiviRes.data : [];
        const derivedSummary = buildSummaryFromCompetences(loadedCompetences);
        setCompetences(loadedCompetences);
        setSummary({
          ...derivedSummary,
          noteValidated: loadedCompetences.some((competence) => competence.noteValidated),
          noteSur20: loadedCompetences.find((competence) => competence.noteValidated)?.noteSur20 ?? derivedSummary.computedNoteSur20,
          validatedAt: loadedCompetences.find((competence) => competence.noteValidated)?.validatedAt || null,
        });
      } catch (err) {
        setError(err.response?.data?.error || "Erreur lors du chargement du suivi.");
      } finally {
        setLoading(false);
      }
    }

    fetchSuivi();
  }, [currentActivite, id, eleve]);

  if (loading) return <Box sx={{ mt: 6, textAlign: "center" }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error" sx={{ m: 4 }}>{error}</Alert>;
  if (!eleve) return <Alert severity="error" sx={{ m: 4 }}>Élève introuvable.</Alert>;

  const validatedNote = summary?.noteValidated ? summary.noteSur20 : null;

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2.5 }, maxWidth: 980, mx: "auto" }}>
      <PageHeader
        title={`Espace élève — ${eleve.prenom} ${eleve.nom}`}
        subtitle="Retrouve tes compétences évaluées et les notes validées par ton professeur."
        homeTo="/"
      />

      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={7}>
            <FormControl fullWidth>
              <InputLabel id="student-activity-label">Activité</InputLabel>
              <Select
                labelId="student-activity-label"
                label="Activité"
                value={currentActivite?._id || ""}
                onChange={(e) => setCurrentActivite(activites.find((a) => a._id === e.target.value) || null)}
              >
                {activites.map((a) => <MenuItem key={a._id} value={a._id}>{a.nom}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={5}>
            <Stack spacing={1} alignItems={{ xs: 'stretch', md: 'flex-end' }}>
              <Chip
                label={summary?.noteValidated ? `Note validée : ${validatedNote}/20` : "Note non validée par le prof"}
                color={summary?.noteValidated ? "success" : "default"}
                sx={{ fontWeight: 700 }}
              />
              <Typography variant="body2" color="text.secondary">
                {summary?.competencesEvaluees || 0}/{summary?.competencesTotal || 0} compétences évaluées
              </Typography>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {!summary?.noteValidated && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Ta note n’apparaît qu’après validation par ton professeur. Tu peux déjà consulter les compétences évaluées.
        </Alert>
      )}

      <Grid container spacing={2}>
        {competences.map((competence) => (
          <Grid item xs={12} md={6} key={competence._id}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
              <Stack spacing={1.5}>
                <Box>
                  <Typography sx={{ fontWeight: 800 }}>{competence.nom}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {competence.isEvaluated ? `${competence.pointsObtenus} / ${competence.valeur} points` : 'En attente d’évaluation'}
                  </Typography>
                </Box>

                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Chip
                    label={competence.isEvaluated ? `${competence.niveau}/5 étoiles` : 'Non évaluée'}
                    color={levelColors[competence.niveau ?? 0]}
                    variant={competence.isEvaluated ? 'filled' : 'outlined'}
                  />
                  {competence.isEvaluated && <Chip label="Compétence évaluée" color="success" variant="outlined" />}
                </Stack>
              </Stack>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
